import "../../styles/main.module.scss";
import { useBillingMetadata } from "../hooks/useBillingMetadata";
import { Calendar } from "../../SharedModule/components/Calendar";
import {
  clear,
  resetFilters,
  resetFiltersWithoutOwner,
  selectFilters,
  setFilters,
  selectEndmonthDate,
  selectMidmonthDate,
  selectRecurringDate,
  selectLastmonthDate,
} from "../redux/reducers/invoices.reducer";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { Select } from "../../SharedModule/components/Select";
import {
  selectClients,
  SelectUsers,
} from "../redux/reducers/metaBilling.reducer";
import {
  APPROVED,
  EXPORTED,
  PENDING,
  RESET,
  ROLE_NOT_OWNERS,
  ROUTE_INVOICES_LIST,
  ROUTE_INVOICES_FILTERS,
  VOID,
  INVOICE_OWNERS_LABEL,
  APPROVED_TEXT,
  PENDING_TEXT,
  EXPORTED_TEXT,
  RESET_TEXT,
  VOID_TEXT,
} from "../../SharedModule/utils/constants";
import { useNavigate, useLocation } from "react-router";
import { DateTime } from "luxon";
import { useIsAllowed } from "../../SharedModule/components/AccessControl";
import Footer from "../../SharedModule/components/Footer";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  billingModule,
  billingModulePerformSearch,
} from "../../SharedModule/utils/accessUtils";
import { Spinner } from "../../SharedModule/components/Spinner";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { loadHolidays } from "../../SharedModule/utils/constants";

const invoiceStatuses = [
  { value: PENDING, label: PENDING_TEXT },
  { value: APPROVED, label: APPROVED_TEXT },
  { value: EXPORTED, label: EXPORTED_TEXT },
  { value: RESET, label: RESET_TEXT },
  { value: VOID, label: VOID_TEXT },
];

export const InvoiceQuery = () => {
  useBillingMetadata();
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters, shallowEqual);
  const users = useSelector(SelectUsers);
  const clients = useSelector(selectClients);
  const navigate = useNavigate();
  const location = useLocation();

  const { isAllowed } = useIsAllowed(ROLE_NOT_OWNERS);
  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);

  const resetPageFilters = () => {
    if (isAllowed) {
      dispatch(resetFilters());
    } else {
      // TO DO: should keep the owner in the filters
      dispatch(resetFiltersWithoutOwner());
    }
  };

  useEffect(() => {
    document.title = "The Hub - Filter by Invoice Details";
  }, []);

  useEffect(() => {
    // Needed for the "Mid Month" filter below (THIS_MONTHS_BILLING).
    loadHolidays();
  }, []);

  useEffect(() => {
    dispatch(clear());
    // clean id filters
    dispatch(
      setFilters({
        internalNumbers: null,
        invoiceNumbers: null,
        idsInputValue: null,
      })
    );
  }, [dispatch, location]);

  enum periods {
    MIDMONTH,
    MONTHEND,
    RECURRING,
    LASTMONTH,
  }

  const changePeriod = async (period: number) => {
    switch (period) {
      case periods.MIDMONTH:
        await loadHolidays();
        dispatch(selectMidmonthDate());
        break;
      case periods.MONTHEND:
        dispatch(selectEndmonthDate());
        break;
      case periods.RECURRING:
        dispatch(selectRecurringDate());
        break;
      case periods.LASTMONTH:
        dispatch(selectLastmonthDate());
        break;
    }
  };

  const goToInvoices = () => {
    navigate(ROUTE_INVOICES_LIST, { state: { from: ROUTE_INVOICES_FILTERS } });
  };

  const backDefaultInvoices = () => {
    resetPageFilters();
    navigate(ROUTE_INVOICES_LIST);
  };

  return (
    <>
      {!rolesLoaded && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {rolesLoaded && !billingModule(userRolesList) && <ForbiddenPage />}

      {rolesLoaded && billingModule(userRolesList) && (
        <div className="content">
          <div className="d-flex flex-wrap">
            <h2 className="flex-fill">Filter by Invoice Details</h2>
            <button
              className="btn button-secondary"
              onClick={() => backDefaultInvoices()}
            >
              Cancel Filters
            </button>
          </div>
          <form className="card invoice-summary-background p-4 mt-3">
            <div className="row justify-content-center">
              <div className="col-sm-3 col-form-label">Invoice Date</div>
              <div className="col-sm-5">
                <div className="d-flex">
                  <div className="position-relative">
                    <label htmlFor="invoice-date-from" className="form-label">
                      From
                    </label>
                    <Calendar
                      id="invoice-date-from"
                      date={filters.dateFrom}
                      onChange={(date: string) => {
                        dispatch(setFilters({ dateFrom: date }));
                      }}
                    />
                  </div>
                  <div className="position-relative ms-5">
                    <label htmlFor="invoice-date-to" className="form-label">
                      To
                    </label>
                    <Calendar
                      id="invoice-date-to"
                      date={filters.dateTo}
                      onChange={(date: string) => {
                        // End of day because endpoint takes hour.
                        dispatch(
                          setFilters({
                            dateTo: DateTime.fromISO(date, {
                              zone: "utc",
                            }).endOf("day"),
                          })
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row justify-content-center">
              <div className="mt-2 offset-sm-5 col-sm-7">
                <div
                  className="btn button-secondary"
                  role="button"
                  tabIndex={0}
                  onClick={() => changePeriod(periods.MIDMONTH)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      changePeriod(periods.MIDMONTH);
                    }
                  }}
                >
                  Mid Month
                </div>
                <div
                  className="btn button-secondary ms-2"
                  role="button"
                  tabIndex={0}
                  onClick={() => changePeriod(periods.MONTHEND)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      changePeriod(periods.MONTHEND);
                    }
                  }}
                >
                  Month's End
                </div>
                <div
                  className="btn button-secondary ms-2"
                  role="button"
                  tabIndex={0}
                  onClick={() => changePeriod(periods.RECURRING)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      changePeriod(periods.RECURRING);
                    }
                  }}
                >
                  Recurring
                </div>
                <div
                  className="btn button-secondary ms-2"
                  role="button"
                  tabIndex={0}
                  onClick={() => changePeriod(periods.LASTMONTH)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      changePeriod(periods.LASTMONTH);
                    }
                  }}
                >
                  Last Month
                </div>
              </div>
            </div>

            <div className="row mt-3 justify-content-center">
              <label htmlFor="invoice-status" className="col-sm-3 col-form-label">
                Invoice Status
              </label>
              <div className="col-sm-5">
                <Select
                  inputId="invoice-status"
                  isMulti
                  options={invoiceStatuses}
                  value={filters.statusIds}
                  onChange={(statuses: any) => {
                    dispatch(setFilters({ statusIds: statuses }));
                  }}
                />
              </div>
            </div>

            {billingModulePerformSearch(userRolesList) && (
              <div className="row mt-3 justify-content-center">
                <label
                  htmlFor="invoice-owners"
                  className="col-sm-3 col-form-label"
                >
                  {INVOICE_OWNERS_LABEL}
                </label>
                <div className="col-sm-5">
                  <Select
                    inputId="invoice-owners"
                    isMulti
                    options={users}
                    value={filters.invoiceOwners}
                    isLoading={!users}
                    onChange={(owners: any) => {
                      dispatch(setFilters({ invoiceOwners: owners }));
                    }}
                  />
                </div>
              </div>
            )}

            <div className="row mt-3 justify-content-center">
              <label htmlFor="invoice-clients" className="col-sm-3 col-form-label">
                Clients
              </label>
              <div className="col-sm-5">
                <Select
                  inputId="invoice-clients"
                  isMulti
                  options={clients}
                  value={filters.accountIds}
                  isLoading={!clients}
                  onChange={(clients: any) => {
                    dispatch(setFilters({ accountIds: clients }));
                  }}
                />
              </div>
            </div>

            <hr className="separator" />
            <div className="mt-4 footer-filter">
              <div
                className="link-text"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  resetPageFilters();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    resetPageFilters();
                  }
                }}
              >
                Reset Filters
              </div>
              <button
                className="btn btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  goToInvoices();
                }}
              >
                Filter Invoices
              </button>
            </div>
          </form>
          <Footer />
        </div>
      )}
    </>
  );
};
